import { brasaoImpressaoBase64 } from "@/assets/brasaoImpressaoBase64";
import { brasaoIndeaNovoBase64 } from "@/assets/brasaoIndeaNovoBase64";
import { TIPO_INSPECAO } from "@/contexts/cadastro/estabelecimento";
import { RelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAM";
import { STATUS_RAM } from "@/contexts/fiscalizacao/relatorioAM/model/STATUS_RAM";
import { cpfMask } from "@/shared/utils/cpfMask";
import dayjs from "dayjs";
import { forwardRef, useMemo } from "react";
import styles from "./style.module.css";

const tipoFormulario = {
  MONITORIA: "MONITORIA",
  AUDITORIA: "AUDITORIA",
  INSPETOR: "INSPEÇÃO",
} as const;

interface ImpresssaoRelatorioAMProps {
  marcadagua?: string;
  item: RelatorioAM | null;
}

export const ImpresssaoRelatorioAM = forwardRef<
  HTMLDivElement,
  ImpresssaoRelatorioAMProps
>(({ marcadagua, item }, ref) => {
  // Transformar os dados do RelatorioAM para o formato estruturado por seções
  const secoesEstruturadas = useMemo(() => {
    if (!item || !item.itens) return [];

    // Agrupar itens por seção
    const secoesMapa = item.itens.reduce(
      (acc, item) => {
        const secaoId = item.secao?.id ?? 0;
        const secaoNome = item.secao?.nome ?? "";

        if (!acc[secaoId]) {
          acc[secaoId] = {
            secao: secaoNome,
            perguntas: [],
          };
        }

        acc[secaoId].perguntas.push({
          pergunta: item.pergunta?.nome ?? "",
          resposta: item.alternativa?.nome || "",
          observacao: item.resposta, // Caso tenha observação adicional
        });

        return acc;
      },
      {} as Record<
        number,
        {
          secao: string;
          perguntas: {
            pergunta: string;
            resposta: string;
            observacao?: string;
          }[];
        }
      >
    );

    return Object.values(secoesMapa);
  }, [item]);

  return (
    <div ref={ref}>
      <div className="marcadagua">{marcadagua}</div>

      <div className={styles.container}>
        {/* Header Section */}
        <table className={styles.headerTable}>
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "60%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>

          <thead>
            <tr>
              <th colSpan={1}>
                <div className={styles.logo}>
                  <img
                    src={brasaoImpressaoBase64}
                    alt="brasao"
                    style={{
                      width: 125,
                    }}
                  />
                </div>
              </th>

              <th colSpan={1}>
                <h1>GOVERNO DO ESTADO DE MATO GROSSO</h1>

                <h1>
                  SECRETARIA ESTADUAL DE DESENVOLVIMENTO ECONÔMICO - SEDEC
                </h1>

                <h1>INSTITUTO DE DEFESA AGROPECUÁRIA - INDEA/MT</h1>

                <h1>
                  COORDENADORIA DE INSPEÇÃO SANITÁRIA DE PRODUTOS DE ORIGEM
                  ANIMAL
                </h1>

                <h1>CISPOA</h1>
              </th>

              <th colSpan={1}>
                <div className={styles.logo}>
                  <img
                    src={brasaoIndeaNovoBase64}
                    alt="brasao indea"
                    style={{
                      width: 125,
                    }}
                  />
                </div>
              </th>
            </tr>
          </thead>
        </table>

        <table className={styles["content-table"]}>
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>

          <tbody>
            <tr>
              <td colSpan={5} style={{ textAlign: "end", paddingBottom: 20 }}>
                Data Emissão:{" "}
                {item?.dataMonitoria
                  ? dayjs(item?.dataMonitoria).format("DD/MM/YYYY")
                  : dayjs().format("DD/MM/YYYY")}
              </td>
            </tr>

            <tr
              style={{
                backgroundColor: "#000000",
                color: "#ffffff",
              }}
            >
              <td style={{ padding: 4, fontSize: 10 }}>
                Establecimento: {item?.estabelecimento?.nome || ""}
              </td>
              <td style={{ padding: 4, fontSize: 10 }}>
                Localidade:{" "}
                {item?.estabelecimento?.endereco?.municipio?.nome
                  ? `${item.estabelecimento.endereco.municipio.nome}-${item.estabelecimento.endereco.municipio.uf?.sigla}`
                  : ""}
              </td>
              <td style={{ padding: 4, fontSize: 10 }}>
                {item?.estabelecimento
                  ? TIPO_INSPECAO[item.estabelecimento.tipoInspecao]
                  : "SISE"}{" "}
                Nº: {item?.estabelecimento?.numeroInspecao || ""}
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <h1
                  style={{
                    marginTop: 20,
                    backgroundColor: "#C9C9C9",
                    textAlign: "center",
                    fontSize: 16,
                  }}
                >
                  {item?.formulario?.nome || ""} -{" "}
                  {item?.formulario
                    ? tipoFormulario[item.formulario?.tipoFormulario!]
                    : ""}
                </h1>
              </td>
            </tr>

            <tr>
              <td colSpan={3} style={{ paddingTop: 20, paddingBottom: 20 }}>
                <div
                  style={{
                    border: "1px solid black",
                    backgroundColor: "#F9F9F9",
                    padding: 8,
                    fontSize: 10,
                  }}
                >
                  <div style={{ display: "flex", marginBottom: 8 }}>
                    <div style={{ width: "50%" }}>
                      <strong>Codigo: </strong>{" "}
                      {item?.estabelecimento?.numeroInspecao || "-"}
                    </div>
                    <div style={{ width: "50%" }}>
                      <strong>Data: </strong>{" "}
                      {item?.dataMonitoria
                        ? dayjs(item.dataMonitoria).format("DD/MM/YYYY")
                        : "-"}
                    </div>
                  </div>

                  <div style={{ display: "flex", marginBottom: 8 }}>
                    <div style={{ width: "50%" }}>
                      <strong>Monitor/Inspetor: </strong>{" "}
                      {item?.inspetor?.nomePessoa ||
                        item?.inspetor?.nome ||
                        "-"}
                    </div>
                    <div style={{ width: "50%" }}>
                      <strong>Responsavel Tecnico: </strong>{" "}
                      {item?.responsavelTecnico || "-"}
                    </div>
                  </div>

                  <div style={{ display: "flex", marginBottom: 8 }}>
                    <div style={{ width: "50%" }}>
                      <strong>Proprietario: </strong>{" "}
                      {item?.proprietario?.nome || "-"}
                    </div>
                  </div>

                  <div style={{ display: "flex", marginBottom: 8 }}>
                    <div style={{ width: "50%" }}>
                      <strong>Ativo: </strong> {item?.ativo ? "Sim" : "Não"}
                    </div>
                    <div style={{ width: "50%" }}>
                      <strong>Situação: </strong>{" "}
                      {item?.situacao ? STATUS_RAM[item?.situacao] : "-"}
                    </div>
                  </div>

                  <div style={{ display: "flex", marginBottom: 8 }}>
                    <div style={{ width: "100%" }}>
                      <strong>Fiscalizações: </strong>
                      {item?.fiscalizacoes?.map((fisc, index) => (
                        <span key={index}>
                          {index > 0 && ", "}
                          {fisc.data
                            ? new Date(fisc.data).toLocaleDateString("pt-BR")
                            : "-"}
                          ({fisc.horaInicial || "-"} - {fisc.horaFinal || "-"})
                          {fisc.placa && ` - Placa: ${fisc.placa}`}
                        </span>
                      )) || "-"}
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <h2
                  style={{
                    textAlign: "center",
                    fontSize: 12,
                    border: "1px solid black",
                  }}
                >
                  QUESTIONARIO
                </h2>
              </td>
            </tr>

            {secoesEstruturadas.map((secao, indexSecao) => (
              <tr key={indexSecao}>
                <td colSpan={3}>
                  <h3
                    style={{
                      marginTop: 20,
                      backgroundColor: "#C9C9C9",
                      padding: 4,
                    }}
                  >
                    {indexSecao + 1}. {secao.secao}
                  </h3>

                  {secao.perguntas.map((pergunta, indexPergunta) => (
                    <div
                      key={indexPergunta}
                      style={{
                        marginBottom: 10,
                        padding: 8,
                        fontSize: 10,
                      }}
                    >
                      <strong>
                        {indexSecao + 1}.{indexPergunta + 1}.
                      </strong>{" "}
                      {pergunta.pergunta}
                      <div style={{ marginTop: 4 }}>
                        <strong>Resposta: </strong> {pergunta.resposta}
                      </div>
                      {pergunta.observacao && (
                        <div style={{ marginTop: 4, fontStyle: "italic" }}>
                          <strong>Observação: </strong> {pergunta.observacao}
                        </div>
                      )}
                    </div>
                  ))}
                </td>
              </tr>
            ))}

            <tr>
              <td colSpan={3} style={{ paddingTop: 20, fontSize: 10 }}>
                <strong>Ação Fiscal:</strong>
              </td>
            </tr>

            <tr>
              <td colSpan={3} style={{ fontSize: 10 }}>
                {item?.acaoFiscal || "Nenhuma ação fiscal informada."}
              </td>
            </tr>

            <tr>
              <td colSpan={3} style={{ paddingTop: 20, fontSize: 10 }}>
                <strong>Conclusão:</strong>
              </td>
            </tr>

            <tr>
              <td colSpan={3} style={{ fontSize: 10 }}>
                {item?.conclusao || "Nenhuma conclusão informada."}
              </td>
            </tr>

            <tr>
              <td colSpan={3} style={{ paddingTop: 20, fontSize: 10 }}>
                <strong>Observações:</strong>
              </td>
            </tr>

            <tr>
              <td colSpan={3} style={{ fontSize: 10 }}>
                {item?.observacao || "Nenhuma observação adicional."}
              </td>
            </tr>
          </tbody>
        </table>

        <table className={styles.tableAssinatura} style={{ marginTop: 40 }}>
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "50%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan={2}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 20,
                  }}
                >
                  {item?.assinaturas.map((assinatura, index) => {
                    return (
                      <div key={index} style={{ width: 200 }}>
                        <div
                          className={styles.containerAssinatura}
                          style={{ width: 200, textAlign: "center" }}
                        >
                          {/* Tratamento dos diferentes status de assinatura */}
                          {assinatura.statusAssinatura === "AUSENTE" && (
                            <span style={{ fontWeight: "bold" }}>AUSENTE</span>
                          )}

                          {assinatura.statusAssinatura ===
                            "IMPOSSIBILITADO" && (
                            <span style={{ fontWeight: "bold" }}>
                              IMPOSSIBILITADO
                            </span>
                          )}

                          {assinatura.statusAssinatura ===
                            "NAO_ALFABETIZADO" && (
                            <span style={{ fontWeight: "bold" }}>
                              NAO ALFABETIZADO
                            </span>
                          )}

                          {assinatura.statusAssinatura === "RECUSADO" && (
                            <span style={{ fontWeight: "bold" }}>RECUSADO</span>
                          )}

                          {/* Exibe a assinatura se estiver presente e não for um dos status especiais */}
                          {assinatura.assinatura &&
                            ![
                              "AUSENTE",
                              "IMPOSSIBILITADO",
                              "NAO_ALFABETIZADO",
                              "RECUSADO",
                            ].includes(assinatura.statusAssinatura) && (
                              <img
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                                src={assinatura.assinatura}
                                alt={`Assinatura de ${assinatura.nome}`}
                              />
                            )}
                        </div>

                        <div style={{ marginTop: 4, fontSize: 10 }}>
                          <span>
                            <strong>Nome:</strong> {assinatura.nome}
                          </span>
                        </div>

                        <div style={{ marginTop: 4, fontSize: 10 }}>
                          <span>
                            <strong>CPF:</strong>{" "}
                            {cpfMask(assinatura.cpf || "")}
                          </span>
                        </div>

                        {!assinatura.externo && (
                          <div style={{ marginTop: 4, fontSize: 10 }}>
                            <span>
                              <strong>Matricula:</strong> {assinatura.matricula}
                            </span>
                          </div>
                        )}

                        <div style={{ marginTop: 4, fontSize: 10 }}>
                          <span>
                            <strong>Função:</strong> {assinatura.funcao}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});
